import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Printer, Mail, Phone, Camera, Edit3, X, Cross } from "lucide-react";
import { APP_NAME, APP_FOOTER, GPDP_LOGO } from "@/lib/constants";

async function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result); r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function KartuPengurus() {
  const { id } = useParams();
  const { user } = useAuth();
  const [pos, setPos] = useState(null);
  const [riwayat, setRiwayat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ photo_base64: "", photo_url: "", phone: "", email: "", bio: "", is_public: true });
  const [photoMode, setPhotoMode] = useState("base64"); // "base64" | "url"
  const [imgFailed, setImgFailed] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        // Try authenticated first (fuller data), fall back to public
        let data;
        try {
          const res = await api.get(`/positions/${id}`);
          data = res.data.position;
          setRiwayat(res.data.riwayat);
        } catch {
          const res = await api.get(`/public/positions/${id}`);
          data = res.data;
          setRiwayat(res.data.riwayat || []);
        }
        setPos(data);
        setForm({
          photo_base64: data.photo_base64 || "",
          photo_url: data.photo_url || "",
          phone: data.phone || "",
          email: data.email || "",
          bio: data.bio || "",
          is_public: data.is_public ?? true,
        });
        if (data.photo_url) setPhotoMode("url");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Tidak dapat memuat data pengurus");
      } finally { setLoading(false); }
    })();
  }, [id]);

  const canEdit = user && (
    ["SUPER_ADMIN","ADMIN_KLASIS","SEKRETARIS_KLASIS"].includes(user.role) ||
    (pos && user.name === pos.person_name)
  );

  const save = async () => {
    try {
      const payload = { ...form };
      // Only send the field for the active mode; clear the other
      if (photoMode === "url") {
        payload.photo_base64 = null;
      } else {
        payload.photo_url = null;
      }
      await api.patch(`/positions/${id}/profile`, payload);
      toast.success("Profil diperbarui.");
      setEditing(false);
      setImgFailed(false);
      const { data } = await api.get(`/positions/${id}`);
      setPos(data.position); setRiwayat(data.riwayat);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handlePrint = () => window.print();

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Memuat kartu…</div>;
  if (!pos) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Kartu tidak ditemukan.</div>;

  const initials = (pos.person_name || "").split(" ").slice(0,2).map(s=>s[0]).join("");
  const periodeLabel = pos.period_start && pos.period_end
    ? `${new Date(pos.period_start).getFullYear()}–${new Date(pos.period_end).getFullYear()}`
    : "—";

  return (
    <div className="min-h-screen bg-muted/40 py-8">
      {/* Actions (hidden on print) */}
      <div className="max-w-3xl mx-auto px-4 mb-6 flex items-center justify-between print:hidden">
        <div className="flex items-center gap-2">
          <Link to={user ? "/pengurus" : "/public"} className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1" data-testid="link-back-kartu">
            <ArrowLeft className="h-4 w-4" /> Kembali
          </Link>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && !editing && (
            <Button variant="outline" size="sm" className="rounded-full" onClick={()=>setEditing(true)} data-testid="btn-edit-kartu">
              <Edit3 className="h-3.5 w-3.5 mr-1.5" /> Edit
            </Button>
          )}
          <Button size="sm" className="rounded-full" onClick={handlePrint} data-testid="btn-print-kartu">
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Cetak / PDF
          </Button>
        </div>
      </div>

      {/* Card */}
      <div ref={printRef} className="max-w-3xl mx-auto px-4">
        <Card className="overflow-hidden border-2 border-primary/20 shadow-sm">
          <div className="stripe-primary h-2" />
          <CardContent className="relative p-8 sm:p-10 grain" data-testid="kartu-pengurus-body">
            <div
              className="kartu-watermark"
              style={{ "--kartu-watermark-image": `url(${GPDP_LOGO})` }}
              aria-hidden="true"
              data-testid="kartu-pengurus-watermark"
            />
            <div className="flex items-start gap-6 flex-wrap">
              {/* Photo */}
              <div className="relative shrink-0">
                {(pos.photo_url || pos.photo_base64) && !imgFailed ? (
                  <img
                    src={pos.photo_url || pos.photo_base64}
                    alt={pos.person_name}
                    className="h-32 w-32 rounded-md object-cover border-2 border-primary/30"
                    onError={() => setImgFailed(true)}
                    onLoad={() => setImgFailed(false)}
                    data-testid="img-kartu-photo"
                  />
                ) : (
                  <div className="h-32 w-32 rounded-md bg-primary text-primary-foreground flex items-center justify-center font-heading text-3xl font-bold border-2 border-primary/30" data-testid="img-kartu-initials">
                    {initials}
                  </div>
                )}
                {editing && photoMode === "base64" && (
                  <label className="absolute bottom-1 right-1 bg-secondary text-secondary-foreground rounded-full p-1.5 cursor-pointer shadow-sm hover:scale-110 transition-transform print:hidden" data-testid="label-photo-upload">
                    <Camera className="h-3 w-3" />
                    <input type="file" accept="image/*" hidden data-testid="input-photo-upload"
                      onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        if (f.size > 1_500_000) { toast.error("Foto terlalu besar (maks 1.5MB)"); return; }
                        setForm({...form, photo_base64: await fileToBase64(f), photo_url: ""});
                      }} />
                  </label>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-[0.65rem] tracking-[0.2em] uppercase text-secondary font-semibold">{pos.organization}</div>
                  <span className="text-muted-foreground/50">•</span>
                  <div className="text-[0.65rem] tracking-[0.15em] uppercase text-muted-foreground">Periode {periodeLabel}</div>
                </div>
                <h1 className="font-heading font-bold text-3xl leading-tight text-foreground">{pos.person_name}</h1>
                <div className="mt-2 text-lg font-heading font-semibold text-primary">{pos.title}</div>

                {editing ? (
                  <div className="mt-4 space-y-3 print:hidden">
                    <div className="border border-border rounded-md p-3 bg-muted/30">
                      <Label className="text-xs font-semibold">Foto</Label>
                      <div className="flex items-center gap-1 mt-2 mb-2 rounded-md bg-background p-1 w-fit">
                        <button type="button" onClick={()=>setPhotoMode("base64")} className={`text-xs px-3 py-1 rounded-sm transition-colors ${photoMode==="base64" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="btn-photo-mode-base64">Upload File</button>
                        <button type="button" onClick={()=>setPhotoMode("url")} className={`text-xs px-3 py-1 rounded-sm transition-colors ${photoMode==="url" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="btn-photo-mode-url">URL Foto</button>
                      </div>
                      {photoMode === "url" ? (
                        <>
                          <Input
                            type="url" placeholder="https://.../foto.jpg"
                            value={form.photo_url}
                            onChange={(e)=>setForm({...form, photo_url:e.target.value, photo_base64:""})}
                            data-testid="input-photo-url"
                          />
                          <div className="text-[0.65rem] text-muted-foreground mt-1">Tempel link foto dari Cloud/Drive publik (https).</div>
                        </>
                      ) : (
                        <div className="text-[0.65rem] text-muted-foreground">Klik ikon kamera di foto untuk upload (JPG/PNG maks 1.5MB).</div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div><Label className="text-xs">Telepon</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} data-testid="input-kartu-phone" /></div>
                      <div><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} data-testid="input-kartu-email" /></div>
                    </div>
                    <div><Label className="text-xs">Bio singkat</Label><Textarea rows={3} value={form.bio} onChange={(e)=>setForm({...form, bio:e.target.value})} data-testid="input-kartu-bio" /></div>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={form.is_public} onChange={(e)=>setForm({...form, is_public:e.target.checked})} data-testid="checkbox-kartu-public" />
                      Tampilkan kontak di kartu publik
                    </label>
                    <div className="flex gap-2 pt-2">
                      <Button size="sm" onClick={save} className="rounded-full" data-testid="btn-save-kartu">Simpan</Button>
                      <Button size="sm" variant="outline" onClick={()=>setEditing(false)} className="rounded-full"><X className="h-3 w-3 mr-1"/>Batal</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {(pos.phone || pos.email) && (
                      <div className="mt-4 flex flex-col sm:flex-row gap-3 text-sm">
                        {pos.phone && <div className="flex items-center gap-2 text-foreground/80"><Phone className="h-3.5 w-3.5 text-primary" />{pos.phone}</div>}
                        {pos.email && <div className="flex items-center gap-2 text-foreground/80"><Mail className="h-3.5 w-3.5 text-primary" />{pos.email}</div>}
                      </div>
                    )}
                    {pos.bio && <div className="mt-4 text-sm text-muted-foreground leading-relaxed">{pos.bio}</div>}
                  </>
                )}
              </div>
            </div>

            {/* SK reference */}
            {pos.sk_number && (
              <div className="mt-8 pt-6 border-t border-border">
                <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground mb-2">Dasar Pengangkatan</div>
                <div className="text-sm text-foreground">
                  SK Nomor <span className="font-mono font-semibold">{pos.sk_number}</span>
                  {pos.sk_date && <span> tanggal {pos.sk_date}</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-1">Periode {pos.period_start} sampai {pos.period_end || "—"}</div>
              </div>
            )}

            {/* Barcode QR */}
            <div className="mt-8 pt-6 border-t border-border flex items-center gap-4">
              <div className="bg-white p-2 border border-border rounded-md" data-testid="qr-pengurus">
                <QRCodeSVG
                  value={`${window.location.origin}/kartu/${pos.id}`}
                  size={80} level="M" bgColor="#FFFFFF" fgColor="#17365D"
                />
              </div>
              <div className="text-xs">
                <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground font-semibold">Kartu Digital Resmi</div>
                <div className="text-sm font-semibold text-primary mt-0.5">Scan QR untuk lihat kartu online</div>
                <div className="font-mono text-[0.7rem] text-muted-foreground mt-0.5">ID: {pos.id?.slice(0, 8)}…</div>
                {pos.sk_number && <div className="text-[0.65rem] text-muted-foreground">SK {pos.sk_number}</div>}
              </div>
            </div>

            {/* Riwayat */}
            {riwayat.length > 1 && (
              <div className="mt-8 pt-6 border-t border-border">
                <div className="text-[0.65rem] tracking-[0.2em] uppercase text-muted-foreground mb-3">Riwayat Pelayanan</div>
                <div className="space-y-2">
                  {riwayat.map((r) => (
                    <div key={r.id} className={`flex items-start gap-3 py-2 border-l-2 pl-3 ${r.id === pos.id ? "border-primary" : "border-border"}`}>
                      <div className="w-24 shrink-0 text-xs text-muted-foreground">
                        {r.period_start ? new Date(r.period_start).getFullYear() : "—"}
                        {r.period_end && ` – ${new Date(r.period_end).getFullYear()}`}
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">{r.title}</span>
                        <span className="text-muted-foreground"> — {r.organization}</span>
                        {r.sk_number && <span className="text-[0.65rem] text-muted-foreground ml-2 font-mono">({r.sk_number})</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>

          {/* Footer branded */}
          <div className="bg-primary text-primary-foreground px-8 py-4 flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <Cross className="h-4 w-4" />
              <div>
                <div className="font-heading font-bold">{APP_NAME}</div>
                <div className="opacity-80 text-[0.65rem]">{APP_FOOTER}</div>
              </div>
            </div>
            <div className="text-right text-[0.65rem] opacity-80">
              Diterbitkan {new Date().toLocaleDateString("id-ID")}
            </div>
          </div>
        </Card>

        <div className="mt-4 text-[0.7rem] text-muted-foreground text-center print:hidden">
          Kartu ini dapat dicetak sebagai identitas resmi pengurus atau disimpan sebagai PDF.
        </div>
      </div>
    </div>
  );
}
